import { Component, forwardRef, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { FormGroup, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Router } from '@angular/router';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { EventReviewConfiguration } from 'src/app/model/eventReviewConfiguration';
import { ReviewDelegateComponent } from 'src/app/component/review-delegate/review-delegate.component';
import { Submission, Review } from 'src/app/model/paper';
import { SubmissionStatus } from 'src/app/model/paper';
import { ReviewStatus } from 'src/app/enum/review.status';
import { ReviewsService } from 'src/app/service/reviews.service';
import { InfoService } from 'src/app/service/info.service';
import { User } from 'src/app/model/user';
import { NotificationService } from 'src/app/service/notification.service';
import { ReviewsConfigurationService } from 'src/app/service/reviews.configuration.service';
import { ConfirmationDialogComponent } from 'src/app/component/confirmation-dialog/confirmation-dialog.component';
import { ReviewReclaimComponent } from '../review-reclaim/review-reclaim.component';
import { AuthService } from 'src/app/service/auth.service';

const reclaimableStatuses = [ReviewStatus.NOTIFIED, ReviewStatus.CONFIRMED, ReviewStatus.ASSIGNED, ReviewStatus.DECLINED]

@Component({
  selector: 'app-paper-review-actions',
  templateUrl: './paper-review-actions.component.html',
  styleUrls: ['./paper-review-actions.component.scss'],
})
export class PaperReviewActionsComponent implements OnInit {
  @Input() review: Review;
  @Input() reviews: Review[];
  @Input() reviewConfig: EventReviewConfiguration;
  @Input() userIsChair = false;
  @Output() reviewUpdated: EventEmitter<Review> = new EventEmitter<Review>(); 

  userIsReviewer = false;
  userIsAssigner = false;
  canReclaim = false;

  constructor(
    private authService: AuthService,
    private reviewsService: ReviewsService,
    private dialog: MatDialog,
    private infoService: InfoService,
    private reviewConfigurationService: ReviewsConfigurationService,
    private notificationService: NotificationService,
    private router: Router,
  ) { }

  ngOnInit(){
    this.VerifyPermissions();
  }

  public VerifyPermissions(){
    this.userIsReviewer = this.setUserIsReviewer();
    this.userIsAssigner = this.setUserIsAssigner();
    this.canReclaim = this.setCanReclaim();
    // Admin do sistema (superuser) tem as permissões de chair em qualquer
    // evento, também quando este componente é usado sem `[userIsChair]`
    // (ex.: `review-card-new`).
    this.authService.directUserIsAdmin().subscribe(isAdmin => {
      if (isAdmin && !this.userIsChair) {
        this.userIsChair = true;
        this.canReclaim = this.setCanReclaim();
      }
    });
  }

  public setUserIsReviewer(): boolean {
    const currentUserId = this.infoService.user.id;
    let isReviewer = false;

    if (this.review.user instanceof User) {
      isReviewer = (this.review.user.id === currentUserId);
    } else {
      isReviewer = (this.review.user === currentUserId);
    }
    return isReviewer;
  }

  public setUserIsAssigner(): boolean {
    let isAssigner = false;
    const currentUserId = this.infoService.user.id;

    if (this.review.user instanceof User) {
      isAssigner = (this.review.assignedBy as User).id == currentUserId;
    } else {
      isAssigner = this.review.assignedBy == currentUserId;
    }

    return isAssigner;
  }
  
  setCanReclaim(){
    // User can reclaim the review only if the review`s status is correct and the user is assigner
    // Or, if it is admin or chair
    // If user is reviewer, the button won't appear anyway
    // Além disso: só revisões DELEGADAS/re-atribuídas podem ser recuperadas
    // (`isFirstAssign === false`); a primeira atribuição não é "recuperável".
    return ((reclaimableStatuses.includes(this.review.status) && this.userIsAssigner) || this.userIsChair)
      && !this.userIsReviewer
      && this.review.isFirstAssign === false;
  }

  editReview(event: any): void {
    const editOrCreate =  this.review.wasAnswered() ? 'edit' : 'create';
    const link = `/reviews/${this.review.id}/${editOrCreate}`;    
    if (event.metaKey || event.ctrlKey) {
      window.open(link, '_blank');
    } else {
      this.router.navigate([link]);
    }
  }

  delegateReview(): void {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.autoFocus = false;
    dialogConfig.width = '45rem';
    dialogConfig.height = '23rem';
    dialogConfig.panelClass = 'my-custom-dialog-class';
    dialogConfig.data = { review: this.review, userIsChair: this.userIsChair };
    this.dialog.open(ReviewDelegateComponent, dialogConfig).afterClosed().subscribe(r => {
      if(r){
        this.review = r;
        this.reviewUpdated.emit(r);
        this.VerifyPermissions();
      }
    });
  }

  isReviewBlocked(): boolean {
    return this.review.isReviewBlocked(this.reviewConfig.blockReview, this.userIsChair, this.review.submission?.isAuthor(this.infoService.user.id));
  }

  reclaim() {
    const dialogConfig = {
      'autoFocus': false,
      'width': '45rem',
      'height': '23rem',
      'panelClass': 'my-custom-dialog-class',
      'data': {
        review: this.review,
        reviews: this.reviews,
        userIsChair: this.userIsChair
      }
    };
    this.dialog.open(ReviewReclaimComponent, dialogConfig).afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.reviewsService.reclaimReview(this.review.id).subscribe(r => {
          this.review = r;
          this.reviewUpdated.emit(r);
        }, () => {}, () => {
          this.notificationService.notify('submissions.view.reclaim-review.notify', { params: { id: this.review.id } });
          this.VerifyPermissions();
        });
      }
    });
  }

  delegateBlocked(): boolean {
    return this.isReviewBlocked() || this.review.status == ReviewStatus.COMPLETED || this.review.status == ReviewStatus.DRAFT
      || this.delegationDisabled();
  }

  /**
   * O usuário atual NÃO pode delegar neste evento?
   *
   * Lê `canDelegate`, que o backend calcula por (evento, usuário): junta a
   * config `tpcDelegate` com o papel de quem pergunta — chair e super passam
   * direto (atribuir não é delegar), os demais precisam ser membros do comitê.
   * Um revisor externo, que recebeu a revisão por delegação, não pode repassar.
   *
   * Só esconde quando a resposta é um `false` EXPLÍCITO: `undefined` significa
   * "não sei" (payload antigo) e a UI segue permitindo. A garantia está no
   * servidor (`ReviewAssignView.put`), que recusa com `delegation-disabled`
   * ou `delegation-not-committee`; aqui só evitamos oferecer um botão que
   * seria negado.
   */
  delegationDisabled(): boolean {
    return this.reviewConfig?.canDelegate === false;
  }

  /** Motivo do bloqueio — os dois casos têm explicações bem diferentes para
   *  quem lê: "o evento desligou isso" ≠ "você não é do comitê". */
  delegationDisabledReason(): string {
    return this.reviewConfig?.tpcDelegate === false
      ? 'reviews.delegation-disabled'
      : 'reviews.delegation-not-committee';
  }

  submitAnswer(answer: 'CONFIRMED' | 'DECLINED'): void {
    if (answer === "CONFIRMED") {
      this.saveAnswer(this.review.id, answer);
    } else {
      const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
        data: {
          title: 'reviews.confirm-review-decline-invite',
          content: ''
        }
      }).afterClosed().subscribe(confirmed => {
        if (confirmed) {
          this.saveAnswer(this.review.id, answer);
        }
      });
    }
  }

  saveAnswer(reviewId: number, answer: 'CONFIRMED' | 'DECLINED' ): void {
    this.reviewsService.sendReviewInvitationAnswer(reviewId, answer).subscribe(r => {
      if (r.status === ReviewStatus.CONFIRMED) {
        this.notificationService.notify('reviews.invite-answer-confirmed', { params: { id: this.review.id } });
      } else if (r.status === ReviewStatus.DECLINED) {
        this.notificationService.notify('reviews.invite-answer-rejected', { params: { id: this.review.id } });
      } else {
        this.notificationService.notify('reviews.invite-answer-error');
      }
      // Reflete a resposta no ESTADO LOCAL antes de avisar o pai. Todo o template
      // decide por `this.review.status` (NOTIFIED → Aceitar/Recusar; demais →
      // Editar/Delegar); sem isso o status seguia NOTIFIED e os botões só
      // evoluíam depois de um refresh manual (issue #2371) — era o que o
      // `window.location.reload()` removido daqui mascarava.
      //
      // Mutação IN PLACE (não `this.review = r`): o objeto vem por referência da
      // lista do pai, então atualizá-lo também acerta o chip de status do card,
      // e preservamos os campos que a resposta do convite não devolve
      // (`submission`, `assignedBy`, ...) e dos quais isReviewBlocked() depende.
      this.review.status = r.status;
      this.VerifyPermissions();
      this.reviewUpdated.emit(r);
    });
  }
}
